// Inventory model: 36 slots — 9 hotbar (0-8) + 27 storage (9-35). A slot holds a
// unified item id (a Block id < 100, or a tool/material id from items.ts) and a
// count. Slot-moving logic (clickSlot) lives here so it can be unit-tested.

import { Block } from './blocks';
import { Item, isBlockItem, maxStackOf } from './items';

export const HOTBAR_SIZE = 9;
export const INVENTORY_SIZE = 36;
export const MAX_STACK = 64;

export interface Slot {
  item: number;
  count: number;
}

/**
 * Minecraft-style click on a slot array with a cursor-held stack: pick up, drop,
 * merge or swap. Mutates `slots`, returns the new held stack. Shared by the
 * inventory and the crafting grid.
 */
export function clickStack(slots: (Slot | null)[], index: number, held: Slot | null): Slot | null {
  const slot = slots[index];
  if (held === null) {
    slots[index] = null;
    return slot;
  }
  if (slot === null) {
    slots[index] = held;
    return null;
  }
  if (slot.item === held.item) {
    const move = Math.min(maxStackOf(slot.item) - slot.count, held.count);
    slot.count += move;
    held.count -= move;
    return held.count > 0 ? held : null;
  }
  slots[index] = held;
  return slot;
}

export class Inventory {
  readonly slots: (Slot | null)[] = new Array(INVENTORY_SIZE).fill(null);
  selected = 0;

  constructor() {
    // Starter kit: building blocks, a crafting table, and raw materials so the
    // player can craft the full tool progression at the table (WM-38). A basic
    // wooden tool set is tucked in storage for immediate play.
    const hotbar: Slot[] = [
      { item: Block.Grass, count: 1 },
      { item: Block.Dirt, count: 1 },
      { item: Block.Stone, count: 1 },
      { item: Block.Wood, count: 8 },
      { item: Block.Cobblestone, count: 16 },
      { item: Block.Planks, count: 16 },
      { item: Block.CraftingTable, count: 1 },
      { item: Block.Torch, count: 8 },
      { item: Block.Sand, count: 1 },
    ];
    hotbar.forEach((slot, i) => (this.slots[i] = slot));
    this.slots[9] = { item: Block.Planks, count: 32 };
    this.slots[10] = { item: Item.Stick, count: 16 };
    this.slots[11] = { item: Block.Wood, count: 16 };
    this.slots[12] = { item: Block.Cobblestone, count: 32 };
    this.slots[13] = { item: Item.WoodPickaxe, count: 1 };
    this.slots[14] = { item: Item.WoodAxe, count: 1 };
    this.slots[15] = { item: Item.WoodSword, count: 1 };
  }

  /** The selected item id (block or tool), or null if the slot is empty. */
  selectedItem(): number | null {
    const slot = this.slots[this.selected];
    return slot && slot.count > 0 ? slot.item : null;
  }

  /** The selected item as a placeable Block, or null if it is a tool/empty. */
  selectedBlock(): Block | null {
    const id = this.selectedItem();
    return id !== null && isBlockItem(id) ? id : null;
  }

  /** Add items to an existing stack or the first empty slot (hotbar first). */
  add(item: number, count = 1): void {
    const max = maxStackOf(item);
    for (const slot of this.slots) {
      if (slot && slot.item === item && slot.count < max) {
        const take = Math.min(max - slot.count, count);
        slot.count += take;
        count -= take;
        if (count <= 0) return;
      }
    }
    for (let i = 0; i < this.slots.length && count > 0; i++) {
      if (!this.slots[i]) {
        const take = Math.min(max, count);
        this.slots[i] = { item, count: take };
        count -= take;
      }
    }
  }

  /** Remove one of the selected item; returns true if something was consumed. */
  consumeSelected(): boolean {
    const slot = this.slots[this.selected];
    if (!slot || slot.count <= 0) return false;
    slot.count -= 1;
    if (slot.count <= 0) this.slots[this.selected] = null;
    return true;
  }

  select(index: number): void {
    this.selected = ((index % HOTBAR_SIZE) + HOTBAR_SIZE) % HOTBAR_SIZE;
  }

  scroll(delta: number): void {
    this.select(this.selected + (delta > 0 ? 1 : -1));
  }

  /**
   * Minecraft-style click interaction. Given the stack currently "held" by the
   * cursor (or null), click slot `index`: pick up, drop, merge or swap.
   * Returns the new held stack.
   */
  clickSlot(index: number, held: Slot | null): Slot | null {
    return clickStack(this.slots, index, held);
  }

  /** Drop a held stack back into the first available space (on close). */
  returnHeld(held: Slot | null): void {
    if (held) this.add(held.item, held.count);
  }
}
